import type {
  CandidateRuleSummary,
  GraduationCandidate,
  GraduationCandidates,
} from '@dosfilos/application';

// ─── Candidatos a graduación: UN modelo para la página, el PDF carta, las
// diapositivas y el CSV (PR 2 · Ajustes, PM 2026-10-27) ─────────────────────
// Cada informe cubre UNA vista («con estudios recientes» o «sin estudios
// recientes»). El resumen, la distribución y la lista salen de las MISMAS
// filas, así que su total es el mismo por construcción (y un test lo exige).
// La lista va numerada y agrupada como la distribución, con subtotales y por
// apellido dentro de cada grupo. Sin textos: cada formato pone sus palabras.

/** Con estudios recientes (cursa ahora o cursó en los últimos N años) o sin ellos. */
export type CandidatesView = 'recent' | 'notRecent';

export interface ReportRow {
  /** Número correlativo en la lista de la vista (1, 2, 3…). */
  n: number;
  personId: string;
  firstName: string;
  lastName: string;
  studentCode: string | null;
  program: string;
  eligible: boolean;
  /** Cursos que faltan (0 = ya cumple los requisitos). */
  missing: number;
  required: Array<{ code: string; name: string }>;
  core: number;
  electives: number;
  unitsEarned: number;
  unitsRequired: number;
  lastTerm: string | null;
  inProgress: number;
}

export interface ReportGroup {
  /** Cursos que faltan del grupo (0 = ya cumplen los requisitos). */
  missing: number;
  rows: ReportRow[];
}

export interface CandidatesReport {
  view: CandidatesView;
  near: number;
  /** N de «cursaron en los últimos N años». */
  recentYears: number;
  rules: CandidateRuleSummary[];
  summary: {
    /** Evaluados de ESTA vista (una sola regla para las dos; ver el pie). */
    evaluated: number;
    eligible: number;
    near: number;
    total: number;
  };
  distribution: Array<{ missing: number; count: number }>;
  groups: ReportGroup[];
  total: number;
}

const inView = (c: GraduationCandidate, view: CandidatesView) =>
  view === 'recent' ? c.active !== false : c.active === false;

const byName = (a: ReportRow, b: ReportRow) =>
  a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' }) ||
  a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' }) ||
  a.program.localeCompare(b.program);

export function buildCandidatesReport(
  data: GraduationCandidates,
  opts: { view: CandidatesView; near: number },
): CandidatesReport {
  const near = Math.min(opts.near, data.nearMaxCourses);
  const rows = data.candidates
    .filter((c) => inView(c, opts.view))
    .filter((c) => c.eligible || c.coursesMissing <= near)
    .map(
      (c): ReportRow => ({
        n: 0,
        personId: c.personId,
        firstName: c.firstName,
        lastName: c.lastName,
        studentCode: c.studentCode,
        program: c.credentialName,
        eligible: c.eligible,
        missing: c.eligible ? 0 : c.coursesMissing,
        required: c.eligible
          ? []
          : (c.requiredMissingCourses ?? c.requiredMissing.map((code) => ({ code, name: '' }))),
        core: c.eligible ? 0 : (c.coreMissing ?? c.groupShortfall),
        electives: c.eligible ? 0 : (c.electivesMissing ?? 0),
        unitsEarned: c.unitsEarned,
        unitsRequired: c.unitsRequired,
        lastTerm: c.lastTerm,
        inProgress: c.inProgress,
      }),
    );
  const groups: ReportGroup[] = Array.from({ length: near + 1 }, (_, missing) => ({
    missing,
    rows: rows.filter((r) => r.missing === missing).sort(byName),
  })).filter((g) => g.rows.length > 0);
  let n = 0;
  for (const g of groups) for (const r of g.rows) r.n = ++n;

  const distribution = Array.from({ length: near + 1 }, (_, missing) => ({
    missing,
    count: rows.filter((r) => r.missing === missing).length,
  }));
  const eligible = rows.filter((r) => r.eligible).length;
  const evaluated =
    opts.view === 'recent'
      ? (data.evaluatedActive ?? data.evaluated)
      : (data.evaluatedHistorical ?? 0);
  return {
    view: opts.view,
    near,
    recentYears: data.activeWithinYears ?? 5,
    rules: data.rules ?? [],
    summary: { evaluated, eligible, near: rows.length - eligible, total: rows.length },
    distribution,
    groups,
    total: n,
  };
}

/** Las filas de la lista, en el orden en que se muestran (agrupadas y numeradas). */
export const reportRows = (r: CandidatesReport): ReportRow[] => r.groups.flatMap((g) => g.rows);

// ── CSV (una vista) ─────────────────────────────────────────────────────────
export interface ReportCsvLabels {
  n: string;
  group: string;
  studentCode: string;
  lastName: string;
  firstName: string;
  program: string;
  missing: string;
  required: string;
  core: string;
  electives: string;
  units: string;
  lastTerm: string;
  inProgress: string;
  groupLabel: (missing: number) => string;
}

const cell = (v: unknown): string => {
  const s = String(v ?? '');
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** El CSV de la vista: una fila por persona y programa, en el orden de la lista.
 *  Identifica por código de alumno, nunca por un id interno. Con BOM. */
export function reportCsv(r: CandidatesReport, l: ReportCsvLabels): string {
  const header = [
    l.n,
    l.group,
    l.studentCode,
    l.lastName,
    l.firstName,
    l.program,
    l.missing,
    l.required,
    l.core,
    l.electives,
    l.units,
    l.lastTerm,
    l.inProgress,
  ];
  const lines = reportRows(r).map((x) => [
    x.n,
    l.groupLabel(x.missing),
    x.studentCode ?? '',
    x.lastName,
    x.firstName,
    x.program,
    x.missing,
    x.required.map((c) => `${c.code} ${c.name}`.trim()).join('; '),
    x.core,
    x.electives,
    `${x.unitsEarned}/${x.unitsRequired}`,
    x.lastTerm ?? '',
    x.inProgress,
  ]);
  return '﻿' + [header, ...lines].map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n';
}

/** Recorta un nombre de curso a su «nombre corto» para el papel. */
export const shortName = (name: string, max = 30) =>
  name.length > max ? `${name.slice(0, max - 1).trimEnd()}…` : name;
